import { useCallback, useMemo } from 'react';
import type { OrderRooftopFacts } from '@hopdrive/feature-order-index';

import type { Customers } from '@gql/base-types';
import { checkForPowerRole } from '@features/auth/services/authService';
import { setCustomerConfig, setRooftop, useRooftop, useRooftops, useUser } from '@store';

import { useGetHomeRooftopsQuery } from '../queries/getHomeRooftops.query.generated';
import { homeRooftopsWhere } from '../utils/homeRooftopsWhere';
import type { HomeScopeOption } from '../utils/homeScopeSelection';
import { toRooftopFacts } from '../utils/toRooftopFacts';

export interface HomeScopeState {
  /** The rooftops the viewer can switch between: the working rooftop's organization for a power role, the working rooftop alone otherwise. Drives the Orders by rooftop cards. */
  options: HomeScopeOption[];
  /** Every listed rooftop's facts, for the by-rooftop breakdown query. */
  listed: OrderRooftopFacts[];
  /** The rooftop the page is scoped to (the working rooftop): its facts, for the feed and the Valet counts adapter. */
  rooftops: OrderRooftopFacts[];
  /** The customer ids the hero and product-card queries run over: the working rooftop alone; empty while nothing is known. */
  customerIds: number[];
  /** The working rooftop's id, the one the page is scoped to. */
  workingRooftopId: number | undefined;
  /**
   * Makes a listed rooftop the working rooftop, exactly as the app header's selector does (the store's
   * rooftop and customer config, the remembered id), so every page — Home included — follows. The one
   * write Home makes to the store; Home has no scope of its own beyond the working rooftop.
   */
  selectRooftop: (id: number) => void;
  loading: boolean;
}

const EMPTY_IDS: number[] = [];

/**
 * The scope Home's data and resolver share. Home is always scoped to the working rooftop: the hero
 * and product cards read that rooftop's figures, and the feed shows its orders. A viewer who spans
 * several rooftops also gets the Orders by rooftop cards (from `options`/`listed`), and picking one
 * makes it the working rooftop through `selectRooftop`, so the whole portal follows. There is no
 * "all rooftops" aggregate. The list is Home's own rooftop document for a power role (the working
 * rooftop's organization, narrowed to the rooftops the viewer is linked to unless they are an admin) and
 * the working rooftop alone for everyone else. The linked rooftops are the header selector's list
 * (usertocustomers), not the token's allowed customers, which hold only so many ids.
 */
export function useHomeScope(): HomeScopeState {
  const user = useUser();
  const rooftop = useRooftop();
  const powerRole = checkForPowerRole(user);
  const isAdmin = user?.claims?.['x-hasura-default-role'] === 'admin';
  const linkedRooftops = useRooftops();
  const allowedIds = useMemo(() => (isAdmin ? EMPTY_IDS : (linkedRooftops ?? []).map(r => Number(r.id))), [isAdmin, linkedRooftops]);
  const workingRooftopId = rooftop?.id !== undefined ? Number(rooftop.id) : user?.profile?.customer_id !== undefined && user?.profile?.customer_id !== null ? Number(user.profile.customer_id) : undefined;
  const organizationId = rooftop?.organization?.id !== undefined && rooftop?.organization?.id !== null ? Number(rooftop.organization.id) : undefined;

  const allowedKey = allowedIds.join(',');
  const where = useMemo(
    () => (workingRooftopId === undefined ? undefined : homeRooftopsWhere({ organizationId, allowedIds, workingRooftopId })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids' content (allowedKey), not the array identity, is the input
    [organizationId, allowedKey, workingRooftopId],
  );
  const { data, loading } = useGetHomeRooftopsQuery({ variables: { where: where ?? {} }, skip: !powerRole || where === undefined || (!isAdmin && allowedIds.length === 0), fetchPolicy: 'cache-and-network' });

  const listed = useMemo<OrderRooftopFacts[]>(() => {
    if (powerRole) return (data?.customers ?? []).map(row => toRooftopFacts(row));
    return rooftop ? [toRooftopFacts(rooftop)] : [];
  }, [powerRole, data, rooftop]);

  const options = useMemo<HomeScopeOption[]>(() => listed.map(facts => ({ id: facts.id, label: facts.name ?? String(facts.id) })), [listed]);

  // The page scopes to the working rooftop: its facts (from the list, or the store row before the list loads) and its id alone.
  const rooftops = useMemo<OrderRooftopFacts[]>(() => {
    if (workingRooftopId === undefined) return [];
    const fromList = listed.find(facts => facts.id === workingRooftopId);
    if (fromList) return [fromList];
    return rooftop ? [toRooftopFacts(rooftop)] : [];
  }, [listed, workingRooftopId, rooftop]);
  const customerIds = useMemo(() => (workingRooftopId === undefined ? EMPTY_IDS : [workingRooftopId]), [workingRooftopId]);

  const selectRooftop = useCallback(
    (id: number) => {
      const row = data?.customers.find(customer => Number(customer.id) === id);
      if (!row) return;
      // The header's own change handler, step for step: the remembered id, the store's rooftop, its effective config.
      localStorage?.setItem('rooftopId', JSON.stringify(id));
      setRooftop(row as Customers);
      if (row.effective_config) setCustomerConfig(row.effective_config);
    },
    [data],
  );

  return { options, listed, rooftops, customerIds, workingRooftopId, selectRooftop, loading: powerRole && loading && !data };
}
